import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { InputComponent } from "@components/input/input.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateDirective, TranslatePipe } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ProduktgruppDTO } from "~app/common/models/common/ProduktgruppDTO";
import { SortimentDTO } from "~app/common/models/common/SortimentDTO";
import { SortimentDetailResponseDTO } from "~app/common/models/sortiment/SortimentDetailResponseDTO";

import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { SortimentService } from "../sortiment.service";

@Component({
    selector: "rask-sortiment-redigera-dialog",
    templateUrl: "./sortiment-redigera-dialog.component.html",
    styleUrls: ["./sortiment-redigera-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        SelectorComponent,
        CheckboxComponent,
        SaveButtonComponent,
        ButtonComponent,
        TranslateDirective,
        TranslatePipe
    ]
})
export class SortimentRedigeraDialogComponent implements OnInit {
    editForm: UntypedFormGroup = new UntypedFormGroup({});
    isLoaded: boolean;
    produktgrupper: ProduktgruppDTO[];
    sortiment: SortimentDTO;
    sortimentId: any;

    private readonly authenticationService = inject(AuthenticationService);
    private readonly modal = inject(NgbActiveModal);
    private readonly raskBusy = inject(BusyService);
    private readonly sortimentService = inject(SortimentService);

    ProduktgruppFormatter = (pg: ProduktgruppDTO) => pg.namn;

    ProduktgruppOutputFormatter = (pg: ProduktgruppDTO) => pg ? pg.id : undefined;

    canEdit(): boolean {
        if (this.authenticationService.canEdit()) {
            return (!this.editForm.valid || this.editForm.dirty);
        }
        return false;
    }

    dismiss() {
        this.modal.dismiss();
    }

    getSortimentNamn() {
        if (!this.authenticationService.canEdit()) {
            return;
        }

        this.sortimentService.getSortimentText(this.editForm.get("kod").value).subscribe(result => {
            this.editForm.get("namn").setValue(result);
        });
    }

    isNew(): boolean { return this.sortimentId == null; }

    ngOnInit() {
        if (this.sortimentId) {
            this.parse(this.sortimentService.get(this.sortimentId));
        } else {
            this.parse(this.sortimentService.getNewSortiment());
        }
    }

    parse(data: Observable<SortimentDetailResponseDTO>) {
        data.subscribe(result => {
            this.sortiment = result.sortiment;
            this.produktgrupper = result.produktgrupper;
            this.mapFromModel(result.sortiment);
            this.isLoaded = true;
        });
    }

    save() {
        if (this.editForm.valid) {
            const request = { sortiment: this.mapFromForm() };
            this.raskBusy.addWork(this.sortimentService.save(request)).subscribe(result => {
                this.modal.close(result);
            });
        }
    }

    private mapFromForm(): SortimentDTO {
        return {
            aktiv: this.editForm.get("aktiv").value,
            kod: this.editForm.get("kod").value,
            namn: this.editForm.get("namn").value,
            produktgruppId: this.editForm.get("produktgruppId").value,
            id: this.isNew() ? null : this.sortiment.id,
            rowVersion: this.isNew() ? null : this.sortiment.rowVersion
        } as SortimentDTO;
    }

    private mapFromModel(sortiment: SortimentDTO) {
        this.editForm = new UntypedFormGroup({
            kod: new UntypedFormControl({ value: sortiment.kod,
                disabled: !this.isNew() }, [Validators.required]),
            namn: new UntypedFormControl({ value: sortiment.namn,
                disabled: !this.authenticationService.canEdit() }),
            produktgruppId: new UntypedFormControl({ value: sortiment.produktgruppId,
                disabled: !this.authenticationService.canEdit() }, [Validators.required]),
            aktiv: new UntypedFormControl({ value: sortiment.aktiv,
                disabled: !this.authenticationService.canEdit() })
        });
        this.editForm.markAsPristine();
    }
}
